import { useCallback, useEffect, useState } from "react";
import {
  getGreenhouseDevices,
  getGreenhouseTelemetrySeries,
  getGreenhouseLevelSensorSeries,
  startGreenhouseRun,
  stopGreenhouseRun,
  fillPump,
  drainPump,
  stopPump,
  listStressProfiles,
  createStressProfile,
  archiveStressProfile,
  listGreenhouseRuns,
} from "services/greenhouseApi";
import { getTelemetrySeries } from "services/api";
import { createCameraRun, getCameraRun, getCameraRuns, stopCameraRun } from "services/cameraApi";
import { mergeTimeSeries } from "utils/timeSeries";
import { campaignIdFromRunId } from "utils/calibrationCampaign";
import { useStableState } from "utils/stableState";

const STORAGE_KEY = "padihat_selected_greenhouse_controller";
const CAMERA_STORAGE_KEY = "padihat_selected_camera_device";
// Exported so Experiment.jsx's own PadiHAT-device-list poll (useDevices'
// refetch, for RunContextBar's "PadiHAT device" chip) uses the exact same
// cadence as this hook's internal poll below, instead of a second literal
// silently drifting from this one.
export const POLL_MS = 15000;
const RUNS_PAGE_SIZE = 20;
// Ground-truth camera capture interval for an Experiment run: 15 minutes,
// the backend's own floor for camera_runs (900-43200s) - see camera_views.py.
const CAMERA_INTERVAL_MINUTES = 15;
// A CameraRun's images page size for chart overlay: comfortably covers a
// full 15-min-interval run (up to 200 images = ~50 hours) in one request,
// unlike CameraGallery's cursor-paginated browsing use case.
const CAMERA_IMAGES_PAGE_SIZE = 200;

// Resolves the numeric CameraRun id (needed by getCameraRun's pagination
// endpoint) for a GreenhouseRun's soft linked_camera_run_id reference, by
// matching it against the camera device's own run list. Pure/exported for
// testing - the two run-id spaces (GreenhouseRun.run_id vs CameraRun.id)
// only meet here, nowhere else in this file.
// The ground-truth camera and the water-level device must be two different
// devices. If they're ever the same (a real bug found live: the Camera tab
// overwrote the shared selected-device key, leaving the camera MAC equal to
// the water MAC while the UI showed the camera as "None"), starting/stopping
// a run would pause/unpause the camera as if it were a water sensor and stop
// its capture run. Pure/exported for testing.
// Which water-level / camera device to use for `run` (an active_run, last_run or
// runs-list entry from the backend): the run's OWN devices win, because the
// devices a run uses are facts about the run (stored/derived server-side), not
// this browser's saved choice. Blank server values (an older backend, a legacy
// run, or a camera run that doesn't exist yet right after start) fall back to
// the local selection, so nothing regresses.
export function resolveRunDevices(run, localWaterMac, localCameraMac) {
  return {
    waterMac: run?.water_device_mac || localWaterMac || "",
    cameraMac: run?.camera_device_mac || localCameraMac || "",
  };
}

export function cameraConflictsWithWater(cameraDeviceMac, waterDeviceMac) {
  return Boolean(cameraDeviceMac) && Boolean(waterDeviceMac) && cameraDeviceMac === waterDeviceMac;
}

export function resolveCameraRunId(cameraRuns, linkedCameraRunId) {
  if (!linkedCameraRunId) return null;
  const match = (cameraRuns || []).find((run) => run.run_id === linkedCameraRunId);
  return match ? match.id : null;
}

// The fields decision #3 named explicitly: environment (target/current
// temp+humidity, from the greenhouse controller) as the independent
// variable, PadiHAT's own power/internal-temp/internal-humidity/water-level
// as the dependent variable it's correlated against.
// water_level_cm is derived by the backend (offset - calibrated distance, ground = 0), NOT d1_cal, which is
// the distance from the sensor to the water. water_level_est_cm is the same level from our own range estimate
// (offset - d1_est); null for a device with no estimate. Battery power is no longer charted here (the Ad-hoc
// panel still offers it).
// ext_t / ext_h: the sensor's own outside temperature/humidity, which the calibration what-if's temperature and
// humidity corrections use (padihat-backend experiment/methods.py).
const WATER_DEVICE_FIELDS = ["t", "h", "ext_t", "ext_h", "water_level_cm", "water_level_est_cm"];

// Derives one {timeNumeric, stepIndex, stepState} entry per REAL transition
// in the ACTUAL reported step_index/step_state (never the planned profile,
// which can drift from what the scheduler actually did - it resumes/
// deviates in ways worth trusting over an assumed schedule, matching this
// codebase's general preference elsewhere, e.g. the ack_status fix in
// GreenhouseRunForm.jsx). `envSeries` is the raw, already-time-sorted array
// greenhouse_telemetry_series returns (each row has `timestamp`, not
// `timeNumeric` - that conversion happens here, the same way
// mergeTimeSeries does its own).
//
// The very first sample always emits a boundary (there's nothing before it
// to compare against - decided rather than special-cased away, so a chart
// scoped to a single-sample or single-step run still gets one marker at its
// start). Later samples emit a boundary only when step_index (or
// step_state, when step_index is null/absent - greenhouse_telemetry_series
// does not currently serialize step_index at all, only step_state) differs
// from the previous sample's.
//
// byTarget (a calibration campaign's single run, since 21 Oct 2026): each
// condition restarts the controller at step 0, so step_index never changes;
// a change of the reported target temperature/humidity marks the next
// condition instead, and every boundary carries its target. Not for stress
// runs, whose ramps move the target continuously.
export function deriveStepBoundaries(envSeries, { byTarget = false } = {}) {
  const boundaries = [];
  let prev = null;
  for (const row of envSeries || []) {
    const timeNumeric = new Date(row.timestamp).getTime();
    if (Number.isNaN(timeNumeric)) continue;
    const stepIndex = row.step_index ?? null;
    const stepState = row.step_state ?? null;
    const targetTempC = row.target_temp_c ?? null;
    const targetRhPct = row.target_rh_pct ?? null;
    if (byTarget && (targetTempC === null || targetRhPct === null)) continue;
    const changed = byTarget
      ? !prev || targetTempC !== prev.targetTempC || targetRhPct !== prev.targetRhPct
      : !prev || stepIndex !== prev.stepIndex || stepState !== prev.stepState;
    if (changed) boundaries.push(byTarget ? { timeNumeric, stepIndex, stepState, targetTempC, targetRhPct } : { timeNumeric, stepIndex, stepState });
    prev = { stepIndex, stepState, targetTempC, targetRhPct };
  }
  return boundaries;
}

export function useExperiment(waterDeviceMac) {
  const [controllers, setControllers] = useStableState([]);
  const [selectedMac, setSelectedMacState] = useState(
    () => localStorage.getItem(STORAGE_KEY) || ""
  );
  // What this browser last chose for its NEXT run. While a run is active (or
  // being viewed) the run's own devices take over - see resolveRunDevices below.
  const [savedCameraMac, setCameraDeviceMacState] = useState(
    () => localStorage.getItem(CAMERA_STORAGE_KEY) || ""
  );
  const [chartData, setChartData] = useStableState([]);
  const [cameraImages, setCameraImages] = useStableState([]);
  const [stepBoundaries, setStepBoundaries] = useStableState([]);
  const [loading, setLoading] = useState(false);
  const [actionPending, setActionPending] = useState(false);
  // Separate from actionPending above: the pump is a deliberately
  // non-interlocked, independent actuator (no relationship to the stress
  // scheduler on the firmware side), so a pending pump command must not
  // grey out the unrelated run-start/stop button, and vice versa.
  const [pumpActionPending, setPumpActionPending] = useState(false);
  const [pumpConfig, setPumpConfig] = useStableState(null);
  const [error, setError] = useState(null);
  const [profiles, setProfiles] = useState([]);
  // null = "follow live" (today's Phase 1 behavior). Set by RunHistoryList's
  // "View" action; cleared by "Back to live" or by starting a new run.
  const [viewedRunId, setViewedRunId] = useState(null);
  const [runs, setRuns] = useStableState([]);
  const [runsTotal, setRunsTotal] = useState(0);
  const [runsLoading, setRunsLoading] = useState(false);

  const refreshProfiles = useCallback(async () => {
    try {
      const result = await listStressProfiles();
      setProfiles(result.profiles || []);
    } catch (requestError) {
      setError(requestError.message);
    }
  }, []);

  const setSelectedMac = (mac) => {
    setSelectedMacState(mac);
    if (mac) localStorage.setItem(STORAGE_KEY, mac);
    else localStorage.removeItem(STORAGE_KEY);
  };

  const setCameraDeviceMac = (mac) => {
    if (cameraConflictsWithWater(mac, waterDeviceMac)) {
      setError("The ground-truth camera can't be the same device as the water-level device.");
      return;
    }
    setCameraDeviceMacState(mac);
    if (mac) localStorage.setItem(CAMERA_STORAGE_KEY, mac);
    else localStorage.removeItem(CAMERA_STORAGE_KEY);
  };

  // Heal a stale/conflicting selection (e.g. one already sitting in
  // localStorage from before this guard existed) instead of carrying a
  // camera==water state silently.
  useEffect(() => {
    if (cameraConflictsWithWater(savedCameraMac, waterDeviceMac)) {
      setCameraDeviceMacState("");
      localStorage.removeItem(CAMERA_STORAGE_KEY);
    }
  }, [savedCameraMac, waterDeviceMac]);

  const refreshControllers = useCallback(async () => {
    try {
      const result = await getGreenhouseDevices();
      const list = result.devices || [];
      setControllers(list);
      if (result.pump_config) setPumpConfig(result.pump_config);
      const stillExists = list.some((c) => c.mac_slug === selectedMac);
      if (!stillExists && list.length > 0) setSelectedMac(list[0].mac_slug);
    } catch (requestError) {
      setError(requestError.message);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedMac]);

  const activeController = controllers.find((c) => c.mac_slug === selectedMac) || null;

  // The chart is scoped to ONE run, in priority order: an explicitly VIEWED
  // run (looked up from the already-fetched `runs` list - RunHistoryList
  // only ever calls viewRun() with a run_id it just rendered a row for, so
  // it's always present here without a separate lookup request), else the
  // currently-active one (open-ended - `end: null` so live polling keeps
  // extending it as it runs), else the most recently completed one
  // (`last_run`, closed to its own start/end) so there's still something to
  // look at between runs. Nothing present means nothing has ever run on
  // this controller - no window, no data, no fetch.
  const activeRun = activeController?.active_run;
  const lastRun = activeController?.last_run;
  const viewedRun = viewedRunId ? runs.find((r) => r.run_id === viewedRunId) || null : null;
  const chartWindow = viewedRun
    ? {
        runId: viewedRun.run_id,
        start: viewedRun.started_at,
        end: viewedRun.ended_at,
        linkedCameraRunId: viewedRun.linked_camera_run_id || null,
      }
    : activeRun
    ? {
        runId: activeRun.run_id,
        start: activeRun.started_at,
        end: null,
        linkedCameraRunId: activeRun.linked_camera_run_id || null,
      }
    : lastRun
    ? {
        runId: lastRun.run_id,
        start: lastRun.started_at,
        end: lastRun.ended_at,
        linkedCameraRunId: lastRun.linked_camera_run_id || null,
      }
    : null;

  // True only when the operator is explicitly looking at a run OTHER than
  // the live one (viewing the actual active run via its History row is
  // indistinguishable from just following live, so that doesn't count).
  const isViewingPastRun = viewedRunId !== null && viewedRunId !== (activeRun?.run_id ?? null);

  // Two different questions, two answers:
  // - SELECTION (selectors, start/stop, camera status card): the ACTIVE run's
  //   devices while one is running - so a laptop, a phone and a teammate all see
  //   and act on the same devices - else this browser's saved choice for the
  //   next run.
  // - CHART DATA (telemetry + camera photos): the devices of the run the chart
  //   is showing (viewed > active > last), else the saved choice.
  const selectionDevices = resolveRunDevices(activeRun, waterDeviceMac, savedCameraMac);
  const effectiveWaterMac = selectionDevices.waterMac;
  const cameraDeviceMac = selectionDevices.cameraMac;
  const chartDevices = resolveRunDevices(viewedRun || activeRun || lastRun, waterDeviceMac, savedCameraMac);
  const chartWaterMac = chartDevices.waterMac;
  const chartCameraMac = chartDevices.cameraMac;

  const refreshRuns = useCallback(async () => {
    if (!selectedMac) {
      setRuns([]);
      setRunsTotal(0);
      return;
    }
    setRunsLoading(true);
    try {
      const result = await listGreenhouseRuns(selectedMac, { limit: RUNS_PAGE_SIZE, offset: 0 });
      setRuns(result.runs || []);
      setRunsTotal(result.count || 0);
    } catch (requestError) {
      setError(requestError.message);
    } finally {
      setRunsLoading(false);
    }
  }, [selectedMac, setRuns]);

  const loadMoreRuns = useCallback(async () => {
    if (!selectedMac) return;
    setRunsLoading(true);
    try {
      const result = await listGreenhouseRuns(selectedMac, {
        limit: RUNS_PAGE_SIZE,
        offset: runs.length,
      });
      setRuns((prev) => [...prev, ...(result.runs || [])]);
      setRunsTotal(result.count || 0);
    } catch (requestError) {
      setError(requestError.message);
    } finally {
      setRunsLoading(false);
    }
  }, [selectedMac, runs.length, setRuns]);

  const viewRun = useCallback((runId) => setViewedRunId(runId), []);
  const followLive = useCallback(() => setViewedRunId(null), []);

  const refreshChart = useCallback(
    async ({ quiet = false } = {}) => {
      if (!selectedMac || !chartWindow) {
        setChartData([]);
        setStepBoundaries([]);
        setCameraImages([]);
        return;
      }
      if (!quiet) setLoading(true);
      setError(null);
      try {
        const [envResult, waterResult, levelSensorResult] = await Promise.all([
          getGreenhouseTelemetrySeries(selectedMac, { runId: chartWindow.runId, limit: 2000 }),
          chartWaterMac
            ? getTelemetrySeries(
                chartWaterMac,
                WATER_DEVICE_FIELDS,
                chartWindow.start,
                chartWindow.end,
                2000
              )
            : Promise.resolve({ data: [] }),
          // 500, not 2000 like the other two fetches above - this sensor's
          // 2s publish cadence means 2000 points can be just ~1 hour of
          // near-continuous, sub-pixel-spaced samples once merged into a
          // ~600px-wide chart, which made hovering flicker (Recharts'
          // nearest-point hit-testing became unstable with that many
          // overlapping x-positions). The backend's stride-sampling
          // (greenhouse_level_sensor_series) still covers the full window
          // at this lower resolution rather than truncating it.
          getGreenhouseLevelSensorSeries(selectedMac, {
            start: chartWindow.start,
            end: chartWindow.end,
            limit: 500,
          }),
        ]);
        // depth_mm -> pressure_level_cm so it shares units (cm) with water_level_cm
        // (PadiHAT's own calibrated water level, in cm) on the same axis -
        // null depth_mm (the firmware's fault-band check) stays null, never
        // fabricated into a number here either.
        const levelSensorSeries = (levelSensorResult.series || []).map((point) => ({
          ...point,
          pressure_level_cm: point.depth_mm != null ? point.depth_mm / 10 : null,
        }));
        const merged = mergeTimeSeries([
          {
            data: envResult.series || [],
            fields: ["target_temp_c", "current_temp_c", "target_rh_pct", "current_rh_pct"],
          },
          {
            data: waterResult.data || [],
            fields: WATER_DEVICE_FIELDS,
          },
          {
            data: levelSensorSeries,
            fields: ["pressure_level_cm"],
          },
        ]);
        // A poll that brought nothing new keeps the very same arrays, so no chart redraws for it.
        setChartData(merged);
        // Follows whichever run is actually being charted (viewed, if any -
        // see chartWindow above), not hardcoded to the live one.
        setStepBoundaries(
          deriveStepBoundaries(envResult.series || [], { byTarget: campaignIdFromRunId(chartWindow.runId) !== null })
        );

        // Ground-truth camera images for this same run, when one was
        // started alongside it. linked_camera_run_id only names a
        // CameraRun.run_id (a soft cross-repo reference, see the model
        // comment) - resolving it to the numeric CameraRun id that
        // getCameraRun's pagination endpoint actually needs requires
        // listing the camera device's runs first, so this can't join the
        // Promise.all above.
        if (chartCameraMac && chartWindow.linkedCameraRunId) {
          try {
            const cameraRunsResult = await getCameraRuns(chartCameraMac);
            const cameraRunId = resolveCameraRunId(cameraRunsResult.runs, chartWindow.linkedCameraRunId);
            if (cameraRunId) {
              const detail = await getCameraRun(cameraRunId, "", CAMERA_IMAGES_PAGE_SIZE);
              setCameraImages(detail.images || []);
            } else {
              setCameraImages([]);
            }
          } catch {
            // Best-effort overlay: a failure to load ground-truth images shouldn't block the rest of
            // the chart from rendering - and shouldn't wipe the photos and labels already on screen
            // (a one-off network hiccup used to make every marker and label vanish until the next poll).
          }
        } else {
          setCameraImages([]);
        }
      } catch (requestError) {
        setError(requestError.message);
      } finally {
        if (!quiet) setLoading(false);
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [
      selectedMac,
      chartWaterMac,
      chartCameraMac,
      chartWindow?.runId,
      chartWindow?.start,
      chartWindow?.end,
      chartWindow?.linkedCameraRunId,
    ]
  );

  useEffect(() => {
    refreshControllers();
    refreshProfiles();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // A viewedRunId from a previous controller could otherwise coincidentally
  // match a run_id under the newly-selected one; resetting on controller
  // change keeps "viewing a past run" scoped to the controller it was set
  // on. Also (re)loads that controller's run history.
  useEffect(() => {
    setViewedRunId(null);
    refreshRuns();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedMac]);

  useEffect(() => {
    refreshChart();
  }, [refreshChart]);

  useEffect(() => {
    if (!selectedMac) return undefined;
    const timer = window.setInterval(() => {
      refreshControllers();
      refreshChart({ quiet: true });
    }, POLL_MS);
    return () => window.clearInterval(timer);
  }, [refreshChart, refreshControllers, selectedMac]);

  const startRun = useCallback(
    async ({
      runId,
      label,
      steps,
      profileId,
      linkedPadihatRunId,
      sampleMs,
      distanceSamples,
      waterEvents,
      waterStartLevelCm,
      tankMinCm,
      tankMaxCm,
    }) => {
      if (!selectedMac) throw new Error("Select a greenhouse controller first.");
      if (cameraConflictsWithWater(cameraDeviceMac, effectiveWaterMac)) {
        throw new Error("The ground-truth camera can't be the same device as the water-level device.");
      }
      setActionPending(true);
      setError(null);
      try {
        const result = await startGreenhouseRun(selectedMac, {
          runId,
          label,
          linkedPadihatRunId,
          // Also unpauses the water-level device's PDH_LAB_WIFI continuous
          // sampling (if it's running that firmware) so it only samples for
          // the duration of this Experiment run, not from firmware boot.
          waterDeviceMac: effectiveWaterMac,
          // Null when GreenhouseRunForm has no water device linked - see
          // its own submitStart, which only validates/sends these when a
          // waterDeviceMac prop was given.
          sampleMs,
          distanceSamples,
          profileId,
          waterEvents,
          waterStartLevelCm,
          tankMinCm,
          tankMaxCm,
          // Sent alongside profileId for the ad-hoc (no saved profile
          // selected) path; the backend ignores this entirely whenever
          // profileId is set, so a saved profile's steps can never be
          // spoofed by also sending a stale ad-hoc profile object.
          profile: { name: runId, steps },
          // Same run id as the greenhouse/water side, so all three sources
          // (chamber, water sensor, ground-truth camera) share one id -
          // GreenhouseRun only stores this as a soft string reference (see
          // its model comment), it doesn't create the CameraRun itself.
          linkedCameraRunId: cameraDeviceMac ? runId : "",
        });
        if (cameraDeviceMac) {
          // Best-effort, same as the water-device lab-mode unpause above -
          // a camera start failure shouldn't fail the whole Experiment
          // start, but the operator does need to know their ground-truth
          // capture isn't actually running.
          try {
            await createCameraRun(cameraDeviceMac, runId, CAMERA_INTERVAL_MINUTES);
          } catch (cameraError) {
            setError(`Camera run failed to start: ${cameraError.message}`);
          }
        }
        // Starting a run is an explicit "look at this now" action - it
        // should never leave the operator stuck looking at an old run.
        setViewedRunId(null);
        await Promise.all([refreshControllers(), refreshRuns()]);
        return result;
      } finally {
        setActionPending(false);
      }
    },
    [refreshControllers, refreshRuns, selectedMac, effectiveWaterMac, cameraDeviceMac]
  );

  const createProfile = useCallback(
    async ({ name, description, steps, waterEvents }) => {
      const result = await createStressProfile({ name, description, steps, waterEvents });
      await refreshProfiles();
      return result;
    },
    [refreshProfiles]
  );

  const archiveProfile = useCallback(
    async (id) => {
      const result = await archiveStressProfile(id);
      await refreshProfiles();
      return result;
    },
    [refreshProfiles]
  );

  const stopRun = useCallback(async () => {
    if (!selectedMac) return;
    if (cameraConflictsWithWater(cameraDeviceMac, effectiveWaterMac)) {
      setError("The ground-truth camera can't be the same device as the water-level device.");
      return;
    }
    setActionPending(true);
    setError(null);
    try {
      const result = await stopGreenhouseRun(selectedMac, { waterDeviceMac: effectiveWaterMac });
      if (cameraDeviceMac) {
        try {
          const cameraRunsResult = await getCameraRuns(cameraDeviceMac);
          const active = (cameraRunsResult.runs || []).find((run) => run.is_active);
          if (active) await stopCameraRun(active.id);
        } catch (cameraError) {
          setError(`Camera run failed to stop: ${cameraError.message}`);
        }
      }
      await Promise.all([refreshControllers(), refreshRuns()]);
      return result;
    } finally {
      setActionPending(false);
    }
  }, [refreshControllers, refreshRuns, selectedMac, effectiveWaterMac, cameraDeviceMac]);

  // Pump actions - independent of startRun/stopRun above, no run refresh
  // needed (pump isn't run-scoped).
  const pumpFill = useCallback(async (cm) => {
    if (!selectedMac) return;
    setPumpActionPending(true);
    setError(null);
    try {
      const result = await fillPump(selectedMac, { cm });
      await refreshControllers();
      return result;
    } finally {
      setPumpActionPending(false);
    }
  }, [refreshControllers, selectedMac]);

  const pumpDrain = useCallback(
    async (cm) => {
      if (!selectedMac) return;
      setPumpActionPending(true);
      setError(null);
      try {
        const result = await drainPump(selectedMac, { cm });
        await refreshControllers();
        return result;
      } finally {
        setPumpActionPending(false);
      }
    },
    [refreshControllers, selectedMac]
  );

  const pumpStop = useCallback(async () => {
    if (!selectedMac) return;
    setPumpActionPending(true);
    setError(null);
    try {
      const result = await stopPump(selectedMac);
      await refreshControllers();
      return result;
    } finally {
      setPumpActionPending(false);
    }
  }, [refreshControllers, selectedMac]);

  return {
    controllers,
    selectedMac,
    setSelectedMac,
    cameraDeviceMac,
    setCameraDeviceMac,
    // Selection devices (what the locked selectors must show) and the devices
    // of the run the chart is showing - see resolveRunDevices above.
    waterDeviceMac: effectiveWaterMac,
    chartWaterDeviceMac: chartWaterMac,
    chartCameraDeviceMac: chartCameraMac,
    activeController,
    hasActiveRun: Boolean(activeRun),
    chartData,
    cameraImages,
    // Swap one photo's payload in place (e.g. after it was labelled) without refetching the run.
    updateCameraImage: (updated) =>
      setCameraImages((images) => images.map((image) => (image.id === updated.id ? updated : image))),
    chartWindow,
    hasWindow: chartWindow !== null,
    stepBoundaries,
    loading,
    actionPending,
    error,
    setError,
    refresh: () => {
      refreshControllers();
      refreshChart();
      refreshRuns();
    },
    startRun,
    stopRun,
    pumpFill,
    pumpDrain,
    pumpStop,
    pumpActionPending,
    pumpConfig,
    profiles,
    refreshProfiles,
    createProfile,
    archiveProfile,
    runs,
    runsTotal,
    runsLoading,
    loadMoreRuns,
    viewedRunId,
    viewRun,
    followLive,
    isViewingPastRun,
  };
}
